import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { label: 'Forside', href: '/' },
    {
      label: 'Produkter',
      href: '/produkter',
      children: [
        { label: 'Parafon Mineraluld / Rockwool', href: '/produkter/parafon-mineraluld-rockwool' },
        { label: 'Flådeloft', href: '/produkter/flaadeloft' },
        { label: 'AMF Thermatex loftplader', href: '/produkter/amf-thermatex-loftplader' },
        { label: 'Montagesystemer', href: '/produkter/montagesystemer' },
        { label: 'Gitterlofter', href: '/produkter/gitterlofter' },
        { label: 'Bafler', href: '/produkter/bafler' },
        { label: 'Justerbare stropper', href: '/produkter/justerbare-stropper' },
        { label: 'Ventatec og DONN Skinnesystem', href: '/produkter/ventatec-og-donn-skinnesystem' },
      ],
    },
    { label: 'Galleri', href: '/galleri' },
    { label: 'Montageanvisning', href: '/montageanvisning' },
    { label: 'Om os', href: '/om-os' },
    { label: 'Kontakt', href: '/kontakt' },
  ];

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setProductsDropdownOpen(false);
  }, [location.pathname]);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
      {/* Top Utility Bar for B2B Contact Info (kelfae.dk style) */}
      <div className="bg-white text-neutral-700 py-2 text-xs border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap justify-between items-center gap-2 font-medium">
          <div className="flex items-center gap-6 text-neutral-600">
            <span className="flex items-center gap-2">
              <i className="fa-regular fa-building text-[#2A8C52]" aria-hidden="true" />
              <span>CVR: 19837688</span>
            </span>
            <span className="hidden sm:inline-flex items-center gap-2">
              <i className="fa-regular fa-location-dot text-[#2A8C52]" aria-hidden="true" />
              <span>Vejleåvej 1, 2635 Ishøj</span>
            </span>
          </div>

          <div className="flex items-center gap-6">
            <a
              href="tel:43712540"
              className="flex items-center gap-2 text-neutral-800 hover:text-[#2A8C52] transition-colors whitespace-nowrap font-medium"
            >
              <i className="fa-solid fa-phone text-[#2A8C52]" aria-hidden="true" />
              <span>(+45) 43 71 25 40</span>
            </a>
            <a
              href="mailto:jlh@kelfae.dk"
              className="flex items-center gap-2 text-neutral-800 hover:text-[#2A8C52] transition-colors whitespace-nowrap font-medium"
            >
              <i className="fa-regular fa-envelope text-[#2A8C52]" aria-hidden="true" />
              <span>jlh@kelfae.dk</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        {/* Brand Logo */}
        <Link to="/" className="flex items-center gap-3 group focus:outline-none">
          <img
            src="https://assets.auxodev.dk/assets/6ff16b38-fa18-408f-9a5d-f4f108c00990/logo-8.png"
            alt="KELFAE LOFTER Logo"
            className="h-10 sm:h-12 w-auto object-contain transition-all"
          />
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-1">
          {navLinks.map((link) => {
            const active = isActive(link.href);
            if (link.children) {
              return (
                <div
                  key={link.label}
                  className="relative group"
                  onMouseEnter={() => setProductsDropdownOpen(true)}
                  onMouseLeave={() => setProductsDropdownOpen(false)}
                >
                  <Link
                    to={link.href}
                    className={`px-4 py-2 text-sm font-medium transition-all flex items-center gap-2 ${
                      active
                        ? 'text-neutral-900 border-b-2 border-[#7FDCA7] font-semibold'
                        : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100'
                    }`}
                  >
                    <span>{link.label}</span>
                    <i
                      className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                        productsDropdownOpen ? 'rotate-180 text-neutral-900' : 'text-neutral-400'
                      }`}
                      aria-hidden="true"
                    />
                  </Link>

                  {/* Dropdown Menu */}
                  <div
                    className={`absolute left-0 top-full pt-2 w-72 transition-all duration-200 ${
                      productsDropdownOpen
                        ? 'opacity-100 visible translate-y-0'
                        : 'opacity-0 invisible -translate-y-2'
                    }`}
                  >
                    <div className="bg-white text-neutral-900 border border-neutral-200 shadow-xl p-2 space-y-1 rounded-xs">
                      <div className="px-3 py-1.5 text-[11px] font-semibold text-neutral-500 uppercase tracking-wider border-b border-neutral-100 mb-1 font-sans">
                        Produktkategorier
                      </div>
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className={`block px-3 py-2 text-xs font-medium transition-colors rounded-xs ${
                            location.pathname === child.href
                              ? 'bg-[#7FDCA7]/25 text-neutral-950 font-semibold'
                              : 'text-neutral-700 hover:text-neutral-950 hover:bg-neutral-100'
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={link.label}
                to={link.href}
                className={`px-4 py-2 text-sm font-medium transition-all ${
                  active
                    ? 'text-neutral-900 border-b-2 border-[#7FDCA7] font-semibold'
                    : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        {/* Desktop CTA Button (>= 1200px) */}
        <div className="hidden min-[1200px]:flex items-center gap-3">
          <Link
            to="/kontakt"
            className="px-5 py-2.5 bg-[#7FDCA7] hover:bg-[#69C892] text-neutral-950 font-semibold text-sm transition-all shadow-sm flex items-center gap-2"
          >
            <span>Få et tilbud</span>
            <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
          </Link>
        </div>

        {/* Hamburger Toggle Button (Below 1200px) */}
        <button
          type="button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="min-[1200px]:hidden p-2.5 text-neutral-800 hover:bg-neutral-100 transition-colors focus:outline-none"
          aria-label="Åbn navigation"
        >
          <i
            className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`}
            aria-hidden="true"
          />
        </button>
      </div>

      {/* Mobile / Tablet Navigation (Below 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-white text-neutral-900 border-b border-neutral-200 px-4 pt-3 pb-6 space-y-2 shadow-lg">
          {navLinks.map((link) => {
            const active = isActive(link.href);
            if (link.children) {
              return (
                <div key={link.label} className="space-y-1">
                  <div className="flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-neutral-900 bg-neutral-100 rounded-xs">
                    <Link to={link.href} onClick={() => setMobileMenuOpen(false)}>
                      {link.label}
                    </Link>
                    <button
                      type="button"
                      onClick={() => setProductsDropdownOpen(!productsDropdownOpen)}
                      className="p-1 text-neutral-600 hover:text-neutral-950"
                    >
                      <i
                        className={`fa-solid fa-chevron-down text-xs transition-transform ${
                          productsDropdownOpen ? 'rotate-180' : ''
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  </div>

                  <div className="pl-4 space-y-1 border-l-2 border-neutral-200 ml-3">
                    {link.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        className={`block px-3 py-2 text-xs font-medium rounded-xs ${
                          location.pathname === child.href
                            ? 'text-neutral-950 bg-[#7FDCA7]/25 font-semibold'
                            : 'text-neutral-700 hover:text-neutral-950 hover:bg-neutral-100'
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={link.label}
                to={link.href}
                className={`block px-3 py-2.5 text-sm font-medium transition-colors rounded-xs ${
                  active
                    ? 'text-neutral-950 bg-[#7FDCA7]/25 font-semibold'
                    : 'text-neutral-700 hover:text-neutral-950 hover:bg-neutral-100'
                }`}
              >
                {link.label}
              </Link>
            );
          })}

          <div className="pt-4 border-t border-neutral-200 flex flex-col gap-3">
            <a
              href="tel:43712540"
              className="flex items-center justify-center gap-2 py-2.5 border border-neutral-300 bg-neutral-50 text-neutral-900 text-sm font-semibold whitespace-nowrap hover:bg-neutral-100"
            >
              <i className="fa-solid fa-phone text-[#2A8C52]" aria-hidden="true" />
              <span>Ring: (+45) 43 71 25 40</span>
            </a>
            <Link
              to="/kontakt"
              className="flex items-center justify-center gap-2 py-2.5 bg-[#7FDCA7] text-neutral-950 text-sm font-semibold hover:bg-[#69C892]"
            >
              <span>Få uforpligtende tilbud</span>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
